import { PropsWithChildren } from "react";
import {
  Image,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";

import { central, spacing } from "../theme/tokens";
import { CENTRAL_TENANT_FALLBACK_NAME } from "../lib/central-tenant-brand";
import {
  AppShell,
  CentralTenantBrand,
  getCentralTenantLogoSource,
  useCentralTenantBrand,
} from "./layout";
import { Building2 } from "./central-icons";
import { WeHonorLogomark } from "./logomark";
import { MonarchBrandLockup } from "./MonarchBrandLockup";

const DESKTOP_BREAKPOINT = 900;

export function CentralAuthShell({ children }: PropsWithChildren) {
  const { width } = useWindowDimensions();
  const desktop = width >= DESKTOP_BREAKPOINT;
  const resolvedBrand = useCentralTenantBrand();
  const brand =
    resolvedBrand.name === CENTRAL_TENANT_FALLBACK_NAME
      ? {
          logoUrl: null,
          name: "Monarch Funeral Home & Cremation Services",
          shortName: "Monarch",
          useBundledMonarchLogo: true,
        }
      : resolvedBrand;
  const authBrandName = brand.name === CENTRAL_TENANT_FALLBACK_NAME
    ? "WeHonor"
    : brand.name;

  return (
    <AppShell
      accessibilityLabel={`${authBrandName} Central sign in`}
      backgroundColor={styles.page.backgroundColor}
      contentStyle={styles.main}
      role="public"
    >
      <View style={[styles.shell, desktop ? styles.shellDesktop : null]}>
        {desktop ? (
          <View style={styles.brandPane}>
            <Brand brand={brand} inverse />
            <View
              accessible
              accessibilityLabel="central by wehonor"
              style={styles.brandFooter}
            >
              <Text style={styles.brandFooterText}>central by</Text>
              <View style={styles.brandFooterWeHonor}>
                <WeHonorLogomark
                  accentColor={central.text.inverseMuted}
                  color={central.text.inverseMuted}
                  size={20}
                />
                <Text style={styles.brandFooterText}>wehonor</Text>
              </View>
            </View>
          </View>
        ) : null}

        {desktop ? null : <View style={styles.mobileBrand}><Brand brand={brand} /></View>}
        <ScrollView
          style={styles.scrollArea}
          automaticallyAdjustKeyboardInsets
          contentContainerStyle={[
            styles.scrollContent,
            desktop ? styles.scrollContentDesktop : null,
          ]}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.formColumn}>
            <View
              style={[
                styles.formSurface,
                desktop ? styles.formSurfaceDesktop : null,
              ]}
            >
              {children}
            </View>
          </View>
        </ScrollView>
        {desktop ? null : (
          <View accessible accessibilityLabel="WeHonor" style={styles.mobileFooter}>
            <WeHonorLogomark
              accentColor={central.text.secondary}
              color={central.text.secondary}
              size={20}
            />
            <Text style={styles.mobileFooterText}>wehonor</Text>
          </View>
        )}
      </View>
    </AppShell>
  );
}

function Brand({
  brand,
  inverse = false,
}: {
  brand: CentralTenantBrand;
  inverse?: boolean;
}) {
  const logoSource = getCentralTenantLogoSource(brand);
  const usesWeHonorFallback = brand.name === CENTRAL_TENANT_FALLBACK_NAME;
  const accessibleName = usesWeHonorFallback ? "WeHonor" : brand.name;
  return (
    <View
      accessible
      accessibilityLabel={`${accessibleName} Central`}
      style={styles.brand}
    >
      {brand.useBundledMonarchLogo ? (
        <MonarchBrandLockup size={inverse ? "auth" : "public"} inverse={inverse} />
      ) : logoSource ? (
        <Image
          accessibilityLabel={`${brand.name} logo`}
          resizeMode="contain"
          source={logoSource}
          style={styles.tenantLogo}
        />
      ) : usesWeHonorFallback ? (
        <View style={styles.weHonorMark}>
          <WeHonorLogomark
            accentColor={inverse ? central.text.inverseMuted : central.action.primaryPressed}
            color={inverse ? central.text.inverse : central.action.primary}
            size={27}
          />
        </View>
      ) : (
        <View style={[styles.mark, inverse ? styles.markInverse : null]}>
          <Building2
            color={inverse ? central.text.inverse : central.action.primary}
            size={19}
            strokeWidth={1.9}
          />
        </View>
      )}
      {brand.useBundledMonarchLogo ? null : <View style={styles.brandCopy}>
        <Text style={[styles.brandName, inverse ? styles.inverseText : null]}>
          {usesWeHonorFallback ? "wehonor" : brand.shortName}
        </Text>
      </View>}
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: central.surface.canvas,
  },
  main: {
    flex: 1,
  },
  shell: {
    backgroundColor: central.surface.canvas,
    flex: 1,
    minHeight: "100%",
  },
  shellDesktop: {
    flexDirection: "row",
  },
  brandPane: {
    backgroundColor: central.surface.sidebar,
    justifyContent: "flex-start",
    minHeight: "100%",
    paddingHorizontal: 56,
    paddingVertical: 48,
    position: "relative",
    width: "38%",
  },
  brandFooter: {
    alignItems: "center",
    bottom: 48,
    flexDirection: "row",
    gap: spacing.xs,
    left: 56,
    opacity: 0.68,
    position: "absolute",
  },
  brandFooterText: {
    color: central.text.inverseMuted,
    fontFamily: "Manrope_500Medium",
    fontSize: 10,
    letterSpacing: 0.25,
    lineHeight: 16,
  },
  brandFooterWeHonor: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.xxs,
  },
  mobileBrand: { paddingHorizontal: 24, paddingTop: 28, paddingBottom: 12 },
  mobileFooter: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.xxs,
    justifyContent: "center",
    paddingTop: spacing.sm,
    paddingBottom: spacing.lg,
    opacity: 0.65,
  },
  mobileFooterText: {
    color: central.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 20,
  },
  scrollArea: { flex: 1 },
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    paddingHorizontal: 24,
    paddingVertical: 28,
  },
  scrollContentDesktop: {
    alignItems: "center",
    backgroundColor: central.surface.canvas,
    justifyContent: "center",
    paddingHorizontal: 48,
    paddingVertical: 48,
  },
  formColumn: {
    alignSelf: "center",
    gap: 32,
    maxWidth: 440,
    width: "100%",
  },
  formSurface: {
    width: "100%",
  },
  formSurfaceDesktop: {
    backgroundColor: central.surface.raised,
    borderColor: central.border.subtle,
    borderRadius: 14,
    borderWidth: 1,
    padding: 32,
    shadowColor: central.text.primary,
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.06,
    shadowRadius: 30,
  },
  brand: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.sm,
    justifyContent: "center",
    width: "100%",
  },
  mark: {
    alignItems: "center",
    backgroundColor: central.action.primary,
    borderRadius: 9,
    height: 38,
    justifyContent: "center",
    width: 38,
  },
  tenantLogo: {
    height: 38,
    width: 156,
  },
  weHonorMark: {
    alignItems: "center",
    height: 38,
    justifyContent: "center",
    width: 50,
  },
  markInverse: {
    backgroundColor: "rgba(255,255,255,0.10)",
    borderColor: "rgba(255,255,255,0.18)",
    borderWidth: 1,
  },
  brandCopy: {
    gap: 1,
  },
  brandName: {
    color: central.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 15,
    lineHeight: 18,
  },
  inverseText: {
    color: central.text.inverse,
  },
});
